
import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BarChart3, LineChart, Menu, Search, X, LogIn, UserCircle, LogOut, UserPlus, CreditCard } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '@/context/AuthContext';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const { user, profile, signOut, loading } = useAuth();

  // Function to scroll to section and close menu, if on homepage
  const scrollToSection = (sectionId: string) => {
    setIsMenuOpen(false);
    
    if (isHomePage) {
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // If not on homepage, navigate to homepage and then scroll
      navigate('/?section=' + sectionId);
    }
  };

  // Function to handle logout with loading state - REMOVED TOAST (handled in AuthContext)
  const handleLogout = async () => {
    if (isLoggingOut) return; // Prevent multiple clicks
    
    setIsLoggingOut(true);
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      // Even if there's an error, force a navigation to login
      navigate('/login');
    } finally {
      setIsLoggingOut(false);
      setIsMenuOpen(false);
    }
  };

  // Function to get user initials
  const getUserInitials = () => {
    if (!profile?.nome) return 'U';
    
    const nameParts = profile.nome.split(' ').filter(part => part.length > 0);
    if (nameParts.length === 0) return 'U';
    
    if (nameParts.length === 1) {
      return nameParts[0].charAt(0).toUpperCase();
    }
    
    return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
  };

  // Handle profile click - don't allow if loading or logging out
  const handleProfileClick = () => {
    if (loading || isLoggingOut) {
      return;
    }
    
    navigate('/admin');
    setIsMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full bg-white/90 backdrop-blur-sm border-b border-gray-100 shadow-sm">
      <div className="container mx-auto flex items-center justify-between h-16 px-4 md:px-6">
        <Link to="/" className="flex items-center gap-2">
          <div className="bg-blue-dark text-white p-1.5 rounded">
            <LineChart className="h-5 w-5" />
          </div>
          <span className="font-bold text-xl text-blue-dark">Broker Base</span>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-6">
          <Link 
            to="/" 
            className="text-gray-700 hover:text-blue-dark font-medium transition-colors"
            onClick={() => scrollToSection('hero')}
          >
            Início
          </Link>
          <Link 
            to="/analisar-imovel" 
            className="text-gray-700 hover:text-blue-dark font-medium transition-colors"
          >
            Analisar Imóvel
          </Link>
          <Link 
            to="/home" 
            className="text-gray-700 hover:text-blue-dark font-medium transition-colors"
          >
            Dashboard
          </Link>
          <button
            onClick={() => scrollToSection('pricing')}
            className="text-gray-700 hover:text-blue-dark font-medium transition-colors"
          >
            Planos
          </button>
        </nav>

        <div className="hidden md:flex items-center gap-4">
          {user ? (
            <>
              <Link to="/home">
                <Button variant="outline" className="text-blue-dark border-blue-dark hover:bg-blue-dark hover:text-white">
                  <LineChart className="mr-2 h-4 w-4" />
                  Acessar Broker Base
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  className="flex items-center gap-2 bg-blue-dark text-white hover:text-white hover:bg-blue-dark/90 border-transparent"
                  onClick={handleProfileClick}
                  disabled={loading || isLoggingOut}
                >
                  <div className="h-6 w-6 rounded-full bg-white text-blue-dark flex items-center justify-center text-xs font-bold">
                    {getUserInitials()}
                  </div>
                  <span className="hidden sm:inline">Meu Perfil</span>
                </Button>
                <Button 
                  variant="ghost" 
                  className="text-gray-700" 
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                >
                  {isLoggingOut ? (
                    <div className="flex items-center">
                      <div className="h-5 w-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2"></div>
                      <span className="hidden sm:inline">Saindo...</span>
                    </div>
                  ) : (
                    <>
                      <LogOut className="h-5 w-5" />
                      <span className="ml-2 hidden sm:inline">Sair</span>
                    </>
                  )}
                </Button>
              </div>
            </>
          ) : (
            <>
              <Button 
                variant="outline" 
                className="text-gray-400 border-gray-300 cursor-not-allowed opacity-60"
                disabled
                title="Sistema temporariamente indisponível"
              >
                <LogIn className="mr-2 h-4 w-4" />
                Entrar
              </Button>
              <Button 
                className="bg-gray-400 cursor-not-allowed opacity-60"
                disabled
                title="Sistema temporariamente indisponível"
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Cadastrar
              </Button>
            </>
          )}
        </div>

        {/* Mobile Navigation */}
        <button
          className="md:hidden text-gray-700"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <Menu className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-b border-gray-100 animate-fade-in absolute w-full">
          <nav className="container mx-auto flex flex-col px-4 py-3 space-y-3">
            <Link 
              to="/" 
              className="px-3 py-2 rounded hover:bg-gray-soft flex items-center"
              onClick={() => {
                setIsMenuOpen(false);
                if (isHomePage) scrollToSection('hero');
              }}
            >
              <span>Início</span>
            </Link>
            <Link 
              to="/analisar-imovel" 
              className="px-3 py-2 rounded hover:bg-gray-soft flex items-center"
              onClick={() => setIsMenuOpen(false)}
            >
              <span>Analisar Imóvel</span>
            </Link>
            <Link 
              to="/dashboard" 
              className="px-3 py-2 rounded hover:bg-gray-soft flex items-center"
              onClick={() => setIsMenuOpen(false)}
            >
              <span>Dashboard</span>
            </Link>
            <button
              onClick={() => scrollToSection('pricing')}
              className="px-3 py-2 rounded hover:bg-gray-soft flex items-center text-left w-full"
            >
              <span>Planos</span>
            </button>
            
            <div className="flex flex-col space-y-2 pt-2 border-t border-gray-100">
              {user ? (
                <>
                  <button 
                    onClick={handleProfileClick}
                    disabled={loading || isLoggingOut}
                    className="flex items-center px-3 py-2 rounded hover:bg-gray-soft text-left disabled:opacity-70"
                  >
                    <UserCircle className="mr-2 h-5 w-5 text-blue-dark" />
                    <span>Meu Perfil</span>
                  </button>
                  <button 
                    onClick={handleLogout}
                    disabled={isLoggingOut}
                    className="flex items-center px-3 py-2 rounded hover:bg-gray-soft text-left disabled:opacity-70"
                  >
                    {isLoggingOut ? (
                      <>
                        <div className="mr-2 h-5 w-5 border-2 border-blue-dark border-t-transparent rounded-full animate-spin"></div>
                        <span>Saindo...</span>
                      </>
                    ) : (
                      <>
                        <LogOut className="mr-2 h-5 w-5 text-blue-dark" />
                        <span>Sair</span>
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    className="w-full text-gray-400 border-gray-300 cursor-not-allowed opacity-60"
                    disabled
                    title="Sistema temporariamente indisponível"
                  >
                    <LogIn className="mr-2 h-4 w-4" />
                    Entrar
                  </Button>
                  <Button 
                    className="w-full bg-gray-400 cursor-not-allowed opacity-60"
                    disabled
                    title="Sistema temporariamente indisponível"
                  >
                    <UserPlus className="mr-2 h-4 w-4" />
                    Cadastrar
                  </Button>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
